// Copyright (c) Docugami, Inc. All rights reserved.

import { DocumentStatus, IngestionStatus } from "networking/api";
import { IObservableCollection, ObservableArray } from "utils/ObservableArray";
import { DgDirectory, FileManager, DgFile, FileProperties } from "./FileManager";

/**
 * File view of all error files
 */
export class ErrorFilesView {
    private readonly _items: ObservableArray<DgFile>;
    private readonly _fileManager: FileManager;
    private readonly _unregister: () => void;
    private _initialized = false;

    get observableItems(): IObservableCollection<DgFile> { return this._items; }
    get items(): Array<DgFile> { return [...this._items.storage]; }
    get initialized(): boolean { return this._initialized; }

    constructor(fileManager: FileManager) {
        this._fileManager = fileManager;

        const initialItems = fileManager.items.filter(item => this.isErrorFile(item)) as DgFile[];
        this._items = new ObservableArray(initialItems, (item1: DgFile, item2: DgFile) => item1.properties.path === item2.properties.path);

        const unregisterAdded = fileManager.onItemAdded.addListener(this.onItemAdded.bind(this));
        const unregisterRemoved = fileManager.onItemRemoved.addListener(this.onItemRemoved.bind(this));
        const unregisterUpdated = fileManager.onFileUpdated.addListener(this.onFileUpdated.bind(this));

        this._unregister = () => {
            unregisterAdded();
            unregisterRemoved();
            unregisterUpdated();
        }
    }

    destructor() {
        this._unregister();
    }

    async initialize(): Promise<Array<DgFile> | null> {
        let result: DgFile[] | null = null;
        if (!this._initialized) {
            result = await this._fileManager.fetchErrorFiles();
            if (!!result) {
                this._initialized = true;
            }
        }
        else {
            result = this.items;
        }

        return result;
    }

    private onItemAdded(item: DgFile | DgDirectory) {
        if (this.isErrorFile(item)) {
            this._items.addOrUpdate(item as DgFile);
        }
    }

    private onItemRemoved(item: DgFile | DgDirectory) {
        if (this.isErrorFile(item)) {
            this._items.remove(item as DgFile);
        }
    }

    private onFileUpdated(updatedEvent: [FileProperties, FileProperties]) {
        const [oldProps, newProps] = updatedEvent;
        const file = this._fileManager.getFile(newProps.path);
        if (!!file && oldProps.ingestionStatus !== newProps.ingestionStatus) {
            if (newProps.ingestionStatus === IngestionStatus.Failed) {
                this._items.addOrUpdate(file);
            }
            else {
                this._items.remove(file);
            }
        }
    }

    private isErrorFile(item: DgFile | DgDirectory) {
        return item instanceof DgFile && (item.properties.status === DocumentStatus.Error || item.properties.status === DocumentStatus.OverQuota);
    }
}
